import React from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChapterCard = ({
  chapter,
  onChapterSelect = () => { },
  className = "",
  quiz,
  handleQuizSelect = () => { }
}) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle2';
      case 'current':
        return 'PlayCircle';
      case 'locked':
        return 'Lock';
      default:
        return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'current':
        return 'var(--color-secondary)';
      case 'locked':
        return 'var(--color-text-muted)';
      default:
        return 'var(--color-text-secondary)';
    }
  };

  const getCardClass = (status) => {
    const baseClass = "group relative bg-surface rounded-xl border transition-all duration-200 ease-out hover-scale";

    switch (status) {
      case 'completed':
        return `${baseClass} border-success-100 hover:border-success-200 hover:shadow-lg cursor-pointer`;
      case 'current':
        return `${baseClass} border-secondary-200 hover:border-secondary-300 hover:shadow-xl cursor-pointer ring-2 ring-secondary-100`;
      case 'locked':
        return `${baseClass} border-border bg-surface-secondary cursor-not-allowed opacity-60`;
      default:
        return `${baseClass} border-border hover:border-border-focus hover:shadow-lg cursor-pointer`;
    }
  };

  const handleCardClick = () => {
    if (chapter.status !== 'locked') {
      onChapterSelect(chapter);
    }
  };

  const getActionButton = () => {
    switch (chapter.status) {
      case 'completed':
        return (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="RotateCcw"
            iconPosition="left"
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            Review Chapter
          </Button>
        );
      case 'current':
        return (
          <div className='flex flex-row gap-10'>
            <Button
              variant="primary"
              size="md"
              fullWidth
              iconName="Play"
              iconPosition="left"
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
              }}
            >
              {chapter.progress > 0 ? 'Continue Learning' : 'Start Chapter'}
            </Button>
            {quiz && (
              <Button
                variant="primary"
                size="md"
                fullWidth
                iconName="Quiz"
                iconPosition="left"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuizSelect(chapter);
                }}
              >
                Take Quiz
              </Button>
            )}
          </div>
        );
      case 'locked':
        return (
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            disabled
            iconName="Lock"
            iconPosition="left"
          >
            Complete Previous Chapter
          </Button>
        );
      default:
        return (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Play"
            iconPosition="left"
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            Start Chapter
          </Button>
        );
    }
  };

  return (
    <div
      className={`${getCardClass(chapter.status)} ${className}`}
      onClick={handleCardClick}
    >
      <div className="p-6">
        {/* Chapter Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`
              flex items-center justify-center w-12 h-12 rounded-lg
              ${chapter.status === 'completed' ? 'bg-success-50' :
                chapter.status === 'current' ? 'bg-secondary-50' : 'bg-surface-secondary'}
            `}>
              <Icon
                name={getStatusIcon(chapter.status)}
                size={24}
                color={getStatusColor(chapter.status)}
                strokeWidth={2}
              />
            </div>
            <div>
              <div className="mb-1 text-sm font-medium text-text-muted">
                Chapter {chapter.id}
              </div>
              <h3 className="text-lg font-semibold font-heading text-text-primary line-clamp-2">
                {chapter.title}
              </h3>
            </div>
          </div>

          {chapter.status === 'current' && (
            <div className="flex items-center px-3 py-1 space-x-1 rounded-full bg-secondary-100">
              <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <span className="text-xs font-medium text-secondary-700">Active</span>
            </div>
          )}

        </div>

        {/* Chapter Description */}
        <p className="mb-4 text-sm text-text-secondary line-clamp-2">
          {chapter.description}
        </p>

        {/* Chapter Metrics */}
        <div className="flex items-center justify-between mb-4 text-sm text-text-secondary">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="BookOpen" size={14} color="currentColor" strokeWidth={2} />
              <span>{chapter.lessons} lessons</span>
            </div>

            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} color="currentColor" strokeWidth={2} />
              <span>{chapter.duration}</span>
            </div>
          </div>

          {chapter.status === 'completed' && (
            <div className="flex items-center space-x-1 text-success-600">
              <Icon name="Trophy" size={14} color="currentColor" strokeWidth={2} />
              <span className="font-medium">{chapter.quizScore}%</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {chapter.status !== 'locked' && (
          <div className="mb-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-text-secondary">Progress</span>
              <span className="text-xs font-medium text-text-primary">{Math.floor(chapter.progress)}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-border-light">
              <div
                className={`h-2 rounded-full progress-animation ${chapter.status === 'completed'
                  ? 'bg-gradient-to-r from-success to-success-600' : 'bg-gradient-to-r from-secondary to-secondary-600'
                  }`}
                style={{ width: `${chapter.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Action Button */}
        {getActionButton()}
      </div>

      {/* Hover Effect Overlay
      {chapter.status !== 'locked' && (
        <div className="absolute inset-0 transition-opacity duration-200 opacity-0 pointer-events-none bg-gradient-to-r from-transparent to-primary-50 group-hover:opacity-100 rounded-xl" />
      )} */}
    </div>
  );
};

export default ChapterCard;